// Contenu de la page d'aide (/aide).
//
// Ajouter une capture d'écran : copier l'image dans frontend/public/help/ (ex. paiement-1.png)
// puis renseigner  image: 'paiement-1.png'  sur l'étape concernée. Sans image, l'étape s'affiche en texte seul.
//
// Ajouter une vidéo (au choix) :
//   video: { youtube: 'ID_DE_LA_VIDEO' }   -> vidéo YouTube "non répertoriée" (recommandé, aucun poids côté serveur)
//   video: { src: 'nom-de-la-video.mp4' }  -> fichier copié dans frontend/public/help/videos/
//
// audiences : qui voit la section. Les valeurs sont les rôles de l'application :
// 'membre' (tout le monde), 'secretaire', 'tresorier', 'pasteur', 'president', 'commissaire', 'editeur', 'admin' (administrateur).

export const HELP_SECTIONS = [
  {
    id: 'membre',
    title: 'Pour tous les membres',
    audiences: ['membre', 'secretaire', 'tresorier', 'pasteur', 'president', 'commissaire', 'editeur', 'admin'],
    guides: [
      {
        id: 'premiere-connexion',
        title: 'Première connexion et mot de passe',
        summary: 'Créer son compte, se connecter, retrouver son mot de passe.',
        steps: [
          { text: 'Ouvrez l\'application dans votre navigateur (ordinateur, tablette ou téléphone) et cliquez sur « Se connecter ».' },
          { text: 'Saisissez votre adresse email et votre mot de passe, puis cliquez sur « Connexion ».' },
          { text: 'Mot de passe oublié ? Cliquez sur « Mot de passe oublié » sous le formulaire : un email avec un lien de réinitialisation vous est envoyé.' },
          { text: 'Pas encore de compte ? Utilisez la demande d\'adhésion : un administrateur la valide, puis vous recevez un lien pour créer votre mot de passe (valable 48 heures).' },
        ],
        tips: ['Choisissez un mot de passe d\'au moins 8 caractères avec majuscules, minuscules, chiffres et un caractère spécial.'],
      },
      {
        id: 'profil',
        title: 'Mon profil et ma carte de membre',
        summary: 'Consulter et mettre à jour vos informations personnelles.',
        steps: [
          { text: 'Cliquez sur « Mon Profil » dans le menu de gauche.' },
          { text: 'Vous pouvez y modifier votre photo, votre téléphone, votre email et votre adresse postale, puis enregistrer.' },
          { text: 'Votre nom, prénom et date de naissance ne se modifient pas seuls : contactez le secrétariat pour ces changements.' },
        ],
      },
      {
        id: 'payer-en-ligne',
        title: 'Payer une cotisation, une dîme ou un don en ligne',
        summary: 'Payer par carte bancaire (Stripe), PayPal ou SumUp.',
        steps: [
          { text: 'Ouvrez « Mes cotisations » dans le menu.' },
          { text: 'Choisissez le type de contribution (cotisation, dîme, offrande, don) et le montant.' },
          { text: 'Choisissez le moyen de paiement : carte bancaire, PayPal ou SumUp. Vous êtes redirigé vers la page sécurisée du prestataire.' },
          { text: 'Une fois le paiement validé, vous revenez sur l\'application et recevez un email de confirmation.' },
          { text: 'Si le paiement n\'aboutit pas, l\'application affiche « Paiement échoué » et aucun montant n\'est débité. Vous pouvez réessayer. La ligne apparaît alors avec le statut « Échoué » dans votre historique.' },
        ],
        tips: ['Vos coordonnées bancaires ne sont jamais enregistrées par l\'application : elles restent chez Stripe, PayPal ou SumUp.'],
      },
      {
        id: 'recus',
        title: 'Retrouver mes paiements et mes reçus fiscaux',
        summary: 'Historique des contributions et reçus pour la déclaration d\'impôts.',
        steps: [
          { text: 'Dans « Mes cotisations », retrouvez toutes vos contributions avec leur date, leur type, leur montant et leur statut.' },
          { text: 'Les statuts possibles : « Payé », « En attente » (saisie par le secrétariat, en cours de validation), « Échoué » (paiement en ligne non abouti), « Refusé », « Remboursé ».' },
          { text: 'Téléchargez votre reçu fiscal depuis la ligne concernée ou depuis le tableau de bord.' },
        ],
      },
      {
        id: 'blog-bible',
        title: 'Blog, Bible interactive et contact du pasteur',
        summary: 'Lire les actualités, la Bible, écrire au pasteur.',
        steps: [
          { text: '« Blog » : lisez les articles et les prédications, y compris les fichiers audio joints.' },
          { text: '« Bible interactive » : consultez la Bible directement dans l\'application.' },
          { text: '« Contacter le pasteur » : envoyez un message privé au pasteur.' },
        ],
      },
    ],
  },
  {
    id: 'secretaire',
    title: 'Secrétaire (SG / SGA)',
    audiences: ['secretaire', 'admin'],
    guides: [
      {
        id: 'saisir-paiement',
        title: 'Enregistrer un paiement manuel (espèces, chèque, virement)',
        summary: 'Saisir une contribution qui sera validée par le trésorier, le président ou le pasteur.',
        steps: [
          { text: 'Ouvrez « Gestion des cotisations » dans le menu Finances.' },
          { text: 'Cliquez sur le bouton d\'ajout, choisissez le membre, le type, le montant, la date et le mode de paiement.' },
          { text: 'Enregistrez : la cotisation est créée avec le statut « En attente ».' },
          { text: 'Elle devient « Payée » une fois validée par le trésorier, le président ou le pasteur.' },
        ],
        tips: [
          'Le statut « En attente » est réservé aux saisies manuelles du secrétariat. Les paiements en ligne qui échouent reçoivent le statut « Échoué », jamais « En attente ».',
        ],
      },
      {
        id: 'gestion-membres',
        title: 'Gérer les membres',
        summary: 'Ajouter, modifier, fusionner les doublons.',
        steps: [
          { text: 'Menu « Membres » > « Gestion des membres » : recherchez un membre, ouvrez sa fiche et modifiez ses informations.' },
          { text: '« Fusion doublons » : repérez les fiches en double et fusionnez-les pour ne garder qu\'un historique unique.' },
        ],
      },
      {
        id: 'demandes-adhesion',
        title: 'Traiter les demandes d\'adhésion',
        summary: 'Valider ou refuser les nouvelles demandes.',
        steps: [
          { text: 'Menu « Membres » > « Demandes d\'adhésion » : la liste des demandes en attente s\'affiche.' },
          { text: 'Vérifiez les informations, puis validez : le candidat reçoit un email pour créer son mot de passe (lien valable 48 heures).' },
        ],
      },
    ],
  },
  {
    id: 'tresorier',
    title: 'Trésorière',
    audiences: ['tresorier', 'admin'],
    guides: [
      {
        id: 'valider-cotisations',
        title: 'Valider les cotisations en attente',
        summary: 'Contrôler les saisies du secrétariat.',
        steps: [
          { text: 'Ouvrez « Gestion des cotisations » et filtrez sur le statut « En attente ».' },
          { text: 'Vérifiez chaque ligne (membre, montant, mode de paiement) par rapport à l\'encaissement réel.' },
          { text: 'Validez la ligne pour la passer en « Payée », ou corrigez-la si une information est fausse.' },
        ],
      },
      {
        id: 'paiements-echoues',
        title: 'Comprendre le statut « Échoué »',
        summary: 'Paiements en ligne (Stripe, PayPal, SumUp) qui n\'ont pas abouti.',
        steps: [
          { text: 'Dans « Gestion des cotisations », le filtre de statut propose « Échoué ».' },
          { text: 'Ces lignes correspondent à des paiements en ligne non aboutis : aucun argent n\'a été encaissé, il n\'y a rien à valider.' },
        ],
      },
      {
        id: 'depenses-compta',
        title: 'Dépenses et comptabilité',
        summary: 'Saisir les dépenses et consulter la comptabilité.',
        steps: [
          { text: 'Menu Finances > « Dépenses » : enregistrez chaque dépense avec sa date, son montant et sa catégorie.' },
          { text: 'Menu Finances > « Comptabilité » : consultez les recettes, les dépenses et le solde.' },
          { text: 'Menu Finances > « Import paiements historiques » : importez des paiements passés depuis un fichier.' },
        ],
      },
      {
        id: 'recu-fiscal-don',
        title: 'Reçus fiscaux des dons en ligne (fête des récoltes)',
        summary: 'Le donateur reçoit automatiquement son reçu fiscal Cerfa 11580.',
        steps: [
          { text: 'Le lien de paiement de la fête des récoltes est hébergé par Stripe : aucun compte n\'est nécessaire pour donner.' },
          { text: 'Après le paiement, le donateur reçoit par email un reçu fiscal officiel (Cerfa 11580) déjà rempli à son nom.' },
          { text: 'Chaque don est conservé dans l\'application avec son numéro d\'ordre (ex. 2026-00001).' },
        ],
      },
    ],
  },
  {
    id: 'pasteur',
    title: 'Pasteur et éditeurs',
    audiences: ['pasteur', 'president', 'editeur', 'admin'],
    guides: [
      {
        id: 'blog-articles',
        title: 'Publier un article de blog',
        summary: 'Rédiger, joindre un fichier audio et publier.',
        steps: [
          { text: 'Ouvrez « Gestion du Blog » et créez un nouvel article.' },
          { text: 'Rédigez le texte, ajoutez une image et, si besoin, un fichier audio (prédication).' },
          { text: 'Choisissez une catégorie puis publiez.' },
        ],
      },
      {
        id: 'blog-categories',
        title: 'Gérer les catégories du blog',
        summary: 'Créer, renommer ou supprimer une catégorie.',
        steps: [
          { text: 'Ouvrez « Catégories du Blog » dans le menu.' },
          { text: 'Ajoutez une catégorie, ou modifiez celles qui existent.' },
        ],
      },
    ],
  },
  {
    id: 'messages-pasteur',
    title: 'Messages des membres',
    audiences: ['pasteur', 'admin'],
    guides: [
      {
        id: 'messages-pasteur',
        title: 'Répondre aux messages des membres',
        summary: 'Lire les messages envoyés via « Contacter le pasteur ».',
        steps: [
          { text: 'Ouvrez « Messages des membres » et lisez chaque message.' },
        ],
      },
    ],
  },
  {
    id: 'admin',
    title: 'Présidente et administrateurs',
    audiences: ['admin'],
    guides: [
      {
        id: 'roles',
        title: 'Rôles et permissions',
        summary: 'Donner à chacun l\'accès dont il a besoin.',
        steps: [
          { text: 'Ouvrez « Rôles & Permissions » dans l\'administration.' },
          { text: 'Choisissez un membre et attribuez-lui un rôle : trésorier, secrétaire, éditeur, pasteur, président, commissaire aux comptes ou administrateur.' },
        ],
        tips: ['Donnez le rôle le plus limité possible : chacun ne voit que ce qui concerne sa fonction.'],
      },
      {
        id: 'stats',
        title: 'Statistiques',
        summary: 'Suivre l\'activité de l\'église.',
        steps: [
          { text: 'Ouvrez « Statistiques » pour consulter les indicateurs de l\'église.' },
        ],
      },
    ],
  },
  {
    id: 'president',
    title: 'Président',
    audiences: ['president', 'admin'],
    guides: [
      {
        id: 'validation-paiements',
        title: 'Valider les paiements saisis par le secrétariat',
        summary: 'Le président peut valider les cotisations « En attente ».',
        steps: [
          { text: 'Dans « Gestion des cotisations », filtrez sur « En attente » et validez les lignes contrôlées.' },
        ],
      },
    ],
  },
  {
    id: 'commissaire',
    title: 'Commissaire aux comptes',
    audiences: ['commissaire', 'admin'],
    guides: [
      {
        id: 'controle-comptes',
        title: 'Contrôler les comptes',
        summary: 'Consulter les finances de l\'église sans rien modifier.',
        steps: [
          { text: 'Ouvrez « Finances » dans le menu : les cotisations, les dépenses et la comptabilité sont en lecture seule.' },
          { text: 'Dans « Comptabilité », consultez le grand livre, la balance, le compte de résultat et les rapports.' },
          { text: 'Les offrandes des cultes sont dans « Culte et offrandes ».' },
        ],
      },
      {
        id: 'rapprochement-bancaire',
        title: 'Faire le rapprochement bancaire',
        summary: 'Associer chaque ligne du relevé à une écriture comptable.',
        steps: [
          { text: 'Dans « Comptabilité », ouvrez l\'onglet « Banque ». S\'il manque un relevé, ajoutez-le dans « Import relevés » (fichier CSV de la banque).' },
          { text: 'Ouvrez ensuite « Rapprochement » et choisissez le relevé.' },
          { text: 'Choisissez un relevé, touchez une ligne du relevé, puis l\'écriture correspondante à droite.' },
          { text: 'Une ligne qui n\'a pas d\'écriture peut être ignorée. L\'icône orange permet de défaire un rapprochement.' },
        ],
        tips: ['Vous ne pouvez pas supprimer un relevé : en cas d\'erreur d\'import, demandez au trésorier de le supprimer.'],
      },
    ],
  },
];
